import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles/tokens.css';
import './styles/base.css';

import { AuthProvider } from './auth/AuthProvider';
import { ThemeSync } from './app/theme';
import { NewVersionBanner } from './app/NewVersionBanner';
import { installDevSignIn } from './lib/devSignIn';
import { router } from './app/routes';
import { consolePathFor, isConsoleHost, newHomeFor } from './auth/home';

installDevSignIn();

// The app moved to standingdocs.com. An old address goes to the new one
// with the path kept, so links already in inboxes still open.
{
  const moved = newHomeFor(window.location);
  if (moved) window.location.replace(moved);
}

// The console has its own host. Its front door is the console, not the
// customer app that happens to share the bundle; anything deeper is left
// alone so a pasted console link still opens. See app/routes.tsx.
{
  const to = isConsoleHost(window.location.hostname) ? consolePathFor(window.location.pathname) : null;
  if (to) window.history.replaceState(null, '', to);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Policy documents change on the order of days, not seconds. Refetching
      // on every window focus would re-read hundreds of documents because
      // somebody switched back from their email.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('No #root element in index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeSync />
        <RouterProvider router={router} />
        <NewVersionBanner />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
